import {
  IDepartment,
  IDepartmentData,
  ICurrency,
  ICurrencyData,
  IGLAccount,
  IGLAccountData,
  IUser,
  IUserData,
  ICountry,
  ICountryData,
  IFieldErrorsData,
  IFieldErrors,
  IMentionCommentData,
  IMentionComment,
  IAttachmentData,
  IAttachment
} from '@/models';

// API Data

export interface IRequisitionSupplierAddressData {
  id?: number;
  address1?: string;
  address2?: string;
  city?: string;
  region?: string;
  country?: ICountryData | string;
  postal_code?: string;
}

export interface IRequisitionSupplierContactData {
  id?: number;
  name?: string;
  email?: string;
  phone?: string;
}

export interface IRequisitionSupplierData {
  id?: number;
  name?: string;
  vendor_code?: string;
  address?: IRequisitionSupplierAddressData;
  contact?: IRequisitionSupplierContactData;
}

export interface IRequisitionLineData {
  id?: number;
  item: string;
  description: string;
  quantity: number;
  unit_price: number | string;
  uom: string;
  total_price?: number;
  account?: IGLAccountData | number | null;
  department?: IDepartmentData | number | null;
}

export interface IRequisitionStatusData {
  value: number;
  display_name: string;
}

export interface IRequisitionAttachmentData {
  id?: number;
  file: string | File;
  filename?: string;
}

export interface IRequisitionCommentData extends IMentionCommentData {
  id?: number;
  owner?: string;
  text?: string;
  attachments?: IAttachmentData[];
  created_at?: Date | string;
  updated_at?: Date | string;
}

export interface IRequisitionApprovalData {
  id?: number;
  status: RequisitionApprovalState;
  approver: IUserData;
  created_at?: Date | string;
  updated_at?: Date | string;
}

export interface IRequisitionData {
  id?: number;
  amount?: number;
  num_items?: number;
  reason?: string;
  note?: string;
  project?: string;
  po_number?: string;
  po_not_required?: boolean;
  status?: IRequisitionStatusData;
  currency?: ICurrencyData | number;
  attachments?: IRequisitionAttachmentData[];
  lines?: IRequisitionLineData[];
  supplier?: IRequisitionSupplierData;
  required_date?: Date | string;
  is_urgent?: boolean;
  created_at?: Date | string;
  updated_at?: Date | string;

  approvers?: IRequisitionApprovalData[];
  buyer?: IUserData | number;
  owner?: IUserData | number;
}

export interface IRequisitionSupplierAddressFieldErrorData {
  address1?: string[];
  address2?: string[];
  city?: string[];
  region?: string[];
  country?: string[];
  postal_code?: string[];
}

export interface IRequisitionSupplierContactFieldErrorData {
  name?: string[];
  email?: string[];
  phone?: string[];
}

export interface IRequisitionSupplierFieldErrorData {
  name?: string[];
  vendor_code?: string[];
  address?: IRequisitionSupplierAddressFieldErrorData;
  contact?: IRequisitionSupplierContactFieldErrorData;
}

export interface IRequisitionLineFieldErrorData {
  item?: string[];
  description?: string[];
  quantity?: string[];
  unit_price?: string[];
  account?: string[];
  department?: string[];
}

export interface IRequisitionFieldErrorData {
  currency?: string[];
  project?: string[];
  reason?: string[];
  note?: string[];
  required_date?: string[];
  po_number?: string[];
  is_urgent?: string[];
  attachments?: IFieldErrorsData[];
  supplier?: IRequisitionSupplierFieldErrorData;
  lines?: IRequisitionLineFieldErrorData[];
}

// Business Logic Interface
export interface IRequisitionSupplierAddress
  extends IRequisitionSupplierAddressData {
  country?: ICountry | string;

  validate(): boolean;
}

export interface IRequisitionSupplierContact
  extends IRequisitionSupplierContactData {
  validate(): boolean;
}

export interface IRequisitionSupplier extends IRequisitionSupplierData {
  address?: IRequisitionSupplierAddress;
  contact?: IRequisitionSupplierContact;

  validate(): boolean;
}

export interface IRequisitionLine extends IRequisitionLineData {
  unit_price: number;
  account: IGLAccount | number | null;
  department: IDepartment | number | null;
  validate(): boolean;
}

export interface IRequisitionStatus extends IRequisitionStatusData {
  getColor(): string;
}

export interface IRequisitionAttachment extends IRequisitionAttachmentData {
  validate(): boolean;
}

export interface IRequisitionApproval extends IRequisitionApprovalData {
  approver: IUser;
  validate(): boolean;
}

type RequisitionComment = IRequisitionCommentData & IMentionComment;
export interface IRequisitionComment extends RequisitionComment {
  validate(): boolean;

  attachments: IAttachment[];

  isCommentEdited: boolean;
}

export interface IRequisition extends IRequisitionData {
  status: IRequisitionStatus;
  currency?: ICurrency | number;
  attachments: IRequisitionAttachment[];
  lines: IRequisitionLine[];
  supplier?: IRequisitionSupplier;

  comments?: IRequisitionComment[];
  approvers?: IRequisitionApproval[];
  buyer?: IUser | number;
  owner?: IUser | number;

  validate(): boolean;

  // Manage requisition lines
  addLine(line: IRequisitionLine): void;
  removeLine(index: number): void;

  // Manage attachments
  attachFile(file: File): void;
  detachFile(file: File): void;
}

export interface IRequisitionSupplierAddressFieldError
  extends IRequisitionSupplierAddressFieldErrorData {
  clearErrors(): void;
}

export interface IRequisitionSupplierContactFieldError
  extends IRequisitionSupplierContactFieldErrorData {
  clearErrors(): void;
}

export interface IRequisitionSupplierFieldError
  extends IRequisitionSupplierFieldErrorData {
  address?: IRequisitionSupplierAddressFieldError;
  contact?: IRequisitionSupplierContactFieldError;

  clearErrors(): void;
}

export interface IRequisitionLineFieldError
  extends IRequisitionLineFieldErrorData {
  clearErrors(): void;
}

export interface IRequisitionFieldError extends IRequisitionFieldErrorData {
  supplier?: IRequisitionSupplierFieldError;
  lines: IRequisitionLineFieldError[];
  attachments: IFieldErrors[];

  clearErrors(): void;
}

// Constants - Used for validation
export const REQUISITION_LINE_ITEM_MAX_DESCRIPTION_LENGTH = 255;

export enum RequisitionStatusEnum {
  Draft = 0,
  Submitted,
  InReview,
  Approval2Pending,
  Approval3Pending,
  Approval4Pending,
  Approved,
  Rejected,
  BuyerAssigned,
  PurchaseInProgress,
  PurchaseDelivered,
  Cancelled,
  Received
}

export enum RequisitionApprovalState {
  TENTATIVE,
  APPROVED,
  REJECTED
}

export interface RequisitionFilterParams {
  state?: RequisitionStatusEnum;
  owner?: number;
  po_number?: string;
}
